import { useLayoutEffect, useState, type RefObject } from "react";

const TAG_GAP_PX = 2;

/**
 * How many tags fit on one trigger row before showing "+N".
 * When `maxCap` is set, uses that fixed count instead of measuring.
 */
export function useFittedVisibleTagCount(
  tagCount: number,
  containerRef: RefObject<HTMLElement | null>,
  measureRootRef: RefObject<HTMLElement | null>,
  maxCap?: number,
): number {
  const [visibleCount, setVisibleCount] = useState(tagCount);

  useLayoutEffect(() => {
    if (maxCap !== undefined) {
      setVisibleCount(Math.min(tagCount, maxCap));
      return;
    }

    const container = containerRef.current;
    const measureRoot = measureRootRef.current;
    if (!container || !measureRoot) {
      setVisibleCount(tagCount);
      return;
    }

    const recalc = () => {
      const available = container.clientWidth;
      if (available <= 0 || tagCount === 0) {
        setVisibleCount(tagCount);
        return;
      }

      const tagEls = measureRoot.querySelectorAll<HTMLElement>("[data-measure-tag]");
      const overflowEl = measureRoot.querySelector<HTMLElement>(
        "[data-measure-overflow]",
      );
      const overflowW = (overflowEl?.offsetWidth ?? 0) + TAG_GAP_PX;

      let used = 0;
      let fit = 0;

      for (let i = 0; i < Math.min(tagEls.length, tagCount); i++) {
        const tagW = tagEls[i].offsetWidth + (i > 0 ? TAG_GAP_PX : 0);
        const remaining = tagCount - (i + 1);
        const reserve = remaining > 0 ? overflowW : 0;

        if (used + tagW + reserve <= available) {
          used += tagW;
          fit = i + 1;
        } else {
          break;
        }
      }

      if (fit === 0 && tagCount > 0) fit = 1;
      setVisibleCount(fit);
    };

    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(container);
    return () => ro.disconnect();
  }, [tagCount, maxCap, containerRef, measureRootRef]);

  return visibleCount;
}
